import React from "react";
import type { TaskBody, TaskCategoryBody, TaskIndexBody, TaskSummary } from "../shared/types";
import { CodeBlock, CodeGroup } from "../components/code";
import { FieldList } from "../components/FieldTable";
import { InlineText } from "../components/InlineText";
import { Callout, Card, Cards } from "../components/blocks";
import { DocsLink } from "../components/DocsLink";
import { Media } from "../components/Media";
import { ChangelogEntries } from "./ChangelogPage";
import { COPY, fill } from "../copy";

/** Task API Reference pages (design §3): index, category, task (the §3.2 template). */

const H2 = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <h2 id={id} className="mt-12 scroll-mt-24 text-head4 font-semibold text-foreground">
    {children}
  </h2>
);

const H3 = ({ children }: { children: React.ReactNode }) => <h3 className="mt-6 text-head6 font-semibold text-foreground">{children}</h3>;

const Bullets = ({ items }: { items: string[] }) => (
  <ul className="mt-2 list-disc space-y-1 pl-6 text-bodyMedium text-foreground">
    {items.map((item) => (
      <li key={item}>
        <InlineText text={item} />
      </li>
    ))}
  </ul>
);

const TaskTable = ({ tasks }: { tasks: TaskSummary[] }) => (
  <div className="my-4 overflow-x-auto">
    <table className="w-full text-left text-bodySmall">
      <thead>
        <tr className="border-b border-border">
          <th className="px-3 py-2">{COPY.task}</th>
          <th className="px-3 py-2">{COPY.category}</th>
          <th className="px-3 py-2">{COPY.description}</th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((t) => (
          <tr key={t.name} className="border-b border-border align-top">
            <td className="px-3 py-2">
              <DocsLink href={t.path} className="text-purpleGreen underline">
                <code>{t.name}</code>
              </DocsLink>
            </td>
            <td className="px-3 py-2 text-muted-foreground">{t.categoryLabel}</td>
            <td className="px-3 py-2">{t.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const TaskIndexPage = ({ body }: { body: TaskIndexBody }) => (
  <div>
    <H2 id="tasks-by-category">{COPY.tasksByCategory}</H2>
    <Cards>
      {body.categories.map((c) => (
        <Card key={c.value} title={c.label} href={c.path}>
          {c.image ? <img src={c.image} alt={c.label} className="mb-2 h-auto w-full rounded" loading="lazy" /> : null}
          {c.description} <span className="whitespace-nowrap">{fill(COPY.tasksCount, { count: c.count })}</span>
        </Card>
      ))}
    </Cards>
    <H2 id="all-tasks">{COPY.allTasks}</H2>
    <TaskTable tasks={body.tasks} />
    {body.gettingStarted.length ? (
      <>
        <H3>{COPY.gettingStarted}</H3>
        <ul className="mt-2 list-disc pl-6 text-bodyMedium">
          {body.gettingStarted.map((p) => (
            <li key={p.path}>
              <DocsLink href={p.path} className="text-purpleGreen underline">
                {p.title}
              </DocsLink>
            </li>
          ))}
        </ul>
      </>
    ) : null}
  </div>
);

export const TaskCategoryPage = ({ body }: { body: TaskCategoryBody }) => (
  <div>
    <H2 id="tasks">{COPY.tasks}</H2>
    <Cards>
      {body.tasks.map((t) => (
        <Card key={t.name} title={t.title} href={t.path}>
          {t.description}
        </Card>
      ))}
    </Cards>
    {body.quickStart ? (
      <>
        <H2 id="quick-start">{COPY.quickStart}</H2>
        <p className="mt-2 text-bodySmall text-muted-foreground">
          <code>{body.quickStart.task}</code>
        </p>
        <CodeGroup samples={body.quickStart.samples} />
      </>
    ) : null}
  </div>
);

export const TaskPage = ({ body }: { body: TaskBody }) => (
  <div>
    {body.deprecated ? <Callout role="warning">{COPY.deprecatedTask}</Callout> : null}
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-bodySmall text-muted-foreground">
      <span>
        <span className="font-semibold text-foreground">{COPY.endpoint}: </span>
        <code>{body.endpoint}</code>
      </span>
      <span>
        <span className="font-semibold text-foreground">{COPY.task}: </span>
        <code>{body.name}</code>
      </span>
      {body.scopes.length ? (
        <span>
          <span className="font-semibold text-foreground">{COPY.scope}: </span>
          <code>{body.scopes.join(", ")}</code>
        </span>
      ) : null}
      {body.category ? (
        <span>
          <span className="font-semibold text-foreground">{COPY.category}: </span>
          <DocsLink href={body.category.path} className="underline">
            {body.category.label}
          </DocsLink>
        </span>
      ) : null}
    </p>

    <H2 id="quick-start">{COPY.quickStart}</H2>
    <CodeGroup samples={body.quickStart} />
    <p className="text-bodySmall text-muted-foreground">{COPY.curlWaitNote}</p>

    {body.examples.length ? (
      <>
        <H2 id="examples">{COPY.examples}</H2>
        {body.examples.map((example) => (
          <div key={example.id} className="mt-6">
            {example.caption ? <p className="text-bodyMedium text-foreground">{example.caption}</p> : null}
            <CodeBlock sample={example.request} title={COPY.request} />
            <div className="grid grid-cols-2 gap-4 clg:grid-cols-4">
              {example.inputs.map((file) => (
                <Media key={file.url} file={file} alt={`${COPY.inputs}: ${file.field ?? ""}`} />
              ))}
              {example.outputs.map((file) => (
                <Media key={file.url} file={file} alt={example.caption || COPY.outputs} />
              ))}
            </div>
          </div>
        ))}
      </>
    ) : null}

    <H2 id="input-schema">{COPY.inputSchema}</H2>
    <FieldList fields={body.input} />

    <H2 id="output-schema">{COPY.outputSchema}</H2>
    <FieldList fields={body.output} />
    <H3>{COPY.requiredExample}</H3>
    <CodeBlock sample={body.requiredExample} />
    <H3>{COPY.fullExample}</H3>
    <CodeBlock sample={body.fullExample} />

    <H2 id="response-example">{COPY.responseExample}</H2>
    <CodeBlock sample={body.responseExample} />
    <H2 id="result-example">{COPY.resultExample}</H2>
    <CodeBlock sample={body.resultExample} />
    {body.exampleNotes ? (
      <p className="text-bodySmall text-muted-foreground">
        <InlineText text={body.exampleNotes} />
      </p>
    ) : null}

    {body.docs.lead ? <p className="mt-10 text-bodyLarge text-foreground">{body.docs.lead}</p> : null}
    {body.docs.built_for ? (
      <>
        <H3>{COPY.builtFor}</H3>
        <Bullets items={body.docs.built_for} />
      </>
    ) : null}
    {body.docs.what_you_get ? (
      <>
        <H3>{COPY.whatYouGet}</H3>
        <Bullets items={body.docs.what_you_get} />
      </>
    ) : null}
    {body.docs.prompting ? (
      <>
        <H3>{COPY.prompting}</H3>
        <p className="mt-2 text-bodyMedium">
          <InlineText text={body.docs.prompting} />
        </p>
      </>
    ) : null}
    {body.docs.specs ? (
      <>
        <H2 id="specs">{COPY.specs}</H2>
        <div className="my-4 overflow-x-auto">
          <table className="w-full text-left text-bodySmall">
            <tbody>
              {body.docs.specs.map((s) => (
                <tr key={s.key} className="border-b border-border align-top">
                  <th className="w-48 px-3 py-2 font-semibold">{s.title}</th>
                  <td className="px-3 py-2">
                    <InlineText text={s.text} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ) : null}
    {body.docs.compares ? (
      <>
        <H3>{COPY.howItCompares}</H3>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-bodyMedium">
          {body.docs.compares.map((c) => (
            <li key={c.task}>
              {fill(COPY.useThisWhen, { text: c.use_this_when })}{" "}
              <DocsLink href={c.path} className="underline">
                {fill(COPY.useOtherWhen, { task: c.task, text: c.use_other_when })}
              </DocsLink>
            </li>
          ))}
        </ul>
      </>
    ) : null}
    {body.docs.errors ? (
      <>
        <H2 id="errors">{COPY.errors}</H2>
        <ul className="mt-2 flex flex-wrap gap-2">
          {body.docs.errors.map((e) => (
            <li key={e.code}>
              <DocsLink href={e.path} className="rounded bg-muted px-2 py-1 text-bodySmall">
                <code>{e.code}</code>
              </DocsLink>
            </li>
          ))}
        </ul>
      </>
    ) : null}
    {body.docs.related ? (
      <>
        <H2 id="related">{COPY.related}</H2>
        <ul className="mt-2 space-y-1 text-bodyMedium">
          {body.docs.related.map((r) => (
            <li key={r.task}>
              <DocsLink href={r.path} className="text-purpleGreen underline">
                <code>{r.task}</code>
              </DocsLink>
              <span className="text-muted-foreground"> · {r.relationText}</span>
            </li>
          ))}
        </ul>
      </>
    ) : null}
    {body.docs.limitations ? (
      <>
        <H2 id="good-to-know">{COPY.limitations}</H2>
        <Bullets items={body.docs.limitations} />
      </>
    ) : null}
    {body.changes.length ? (
      <>
        <H2 id="changes">{COPY.changes}</H2>
        <ChangelogEntries entries={body.changes} />
      </>
    ) : null}
    <p className="mt-10 text-bodyMedium">
      <span className="font-semibold">{COPY.pricing}: </span>
      <DocsLink href={body.pricingPath} className="text-purpleGreen underline">
        {COPY.pricingLine}
      </DocsLink>
    </p>
    <H3>{COPY.forAgents}</H3>
    <ul className="mt-2 space-y-1 text-bodySmall">
      <li>
        <DocsLink href={body.machine.llms} className="underline">
          {body.machine.llms}
        </DocsLink>
      </li>
      <li>
        <DocsLink href={body.machine.openapi} className="underline">
          {body.machine.openapi}
        </DocsLink>
      </li>
      <li>
        <code>{body.machine.entry}</code>
      </li>
    </ul>
    <Callout role="note">{COPY.qualityNote}</Callout>
  </div>
);
